import React, { useEffect, useState } from 'react';
import { getWidgetConnectionKeys, type WidgetManifest, type WidgetSurface } from '../../services/myToolsApi';
import WidgetPermissionList from './WidgetPermissionList';
import { manifestDefaultSurfaces, pendingPermissions, type WidgetInstallControls } from './widgetInstallControls';
import { describeWidgetPermission } from './widgetPermissions';
import { clearWidgetStorage, currentUserKey } from './widgetStorage';
import { WIDGET_INSTALL_DOCS_URL } from './widgetSdkLinks';
import WidgetDocsModal from './WidgetDocsModal';
import '../../styles/Widgets.css';

const SURFACE_LABEL: Record<WidgetSurface, string> = { page: 'Widgets page', sidebar: 'Right sidebar', tile: 'Home screen tile' };

export interface WidgetControlsPanelProps {
  toolId: string;
  installationId: string;
  manifest: WidgetManifest | null | undefined;
  controls: WidgetInstallControls;
  onChange: (next: WidgetInstallControls) => void;
  /** Connection secrets to write on the next Save (key -> new value, or "" to disconnect).
   *  Never pre-filled with the current value — the value never comes back to the browser. */
  connectionValues: Record<string, string>;
  onConnectionValuesChange: (next: Record<string, string>) => void;
}

/** What the person controls about an installed widget: granted permissions, where it shows,
 *  pause, and its stored data. Saved with the installation's configuration. */
const WidgetControlsPanel: React.FC<WidgetControlsPanelProps> = ({ toolId, installationId, manifest, controls, onChange, connectionValues, onConnectionValuesChange }) => {
  const [storageMsg, setStorageMsg] = useState<string | null>(null);
  const [showConnectDocs, setShowConnectDocs] = useState(false);
  const [configuredKeys, setConfiguredKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    const controller = new AbortController();
    getWidgetConnectionKeys(installationId, controller.signal)
      .then(keys => {
        if (!controller.signal.aborted) setConfiguredKeys(new Set(keys));
      })
      // Leaving the tab aborts this request; an unhandled AbortError surfaced as a runtime error
      // (in dev the error overlay then covered the page and swallowed every click).
      .catch(() => { });
    return () => controller.abort();
  }, [installationId]);

  const allPermissions = manifest?.permissions ?? [];
  const granted = new Set(controls.grantedPermissions ?? allPermissions);
  const pending = pendingPermissions(manifest, controls);
  const availableSurfaces = manifestDefaultSurfaces(manifest);
  const shownOn = new Set(controls.surfaces ?? availableSurfaces);

  const togglePermission = (key: string, on: boolean) => {
    const next = new Set(granted);
    if (on) next.add(key); else next.delete(key);
    // Always an explicit list from here on — never collapse back to "whatever the manifest asks".
    onChange({ ...controls, grantedPermissions: allPermissions.filter(p => next.has(p)) });
  };

  const toggleSurface = (surface: WidgetSurface, on: boolean) => {
    const next = new Set(shownOn);
    if (on) next.add(surface); else next.delete(surface);
    const list = availableSurfaces.filter(s => next.has(s));
    onChange({ ...controls, surfaces: list.length === availableSurfaces.length ? null : list });
  };

  const resetStorage = () => {
    clearWidgetStorage(currentUserKey(), toolId);
    setStorageMsg('Stored data cleared. The widget starts fresh next time it loads.');
    setTimeout(() => setStorageMsg(null), 4000);
  };

  return (
    <div className="wg-controls">
      <div className="wg-controls__block">
        <span className="wg-perm-title">Permissions granted to this widget</span>
        {pending.length > 0 && (
          <p className="wg-perm-ack" style={{ marginTop: 0 }}>
            <span>
              A newer version of this widget asks for {pending.length} permission{pending.length === 1 ? '' : 's'} you haven't approved yet
              ({pending.map(p => describeWidgetPermission(p).label).join(', ')}). {pending.length === 1 ? 'It stays' : 'They stay'} off until you tick {pending.length === 1 ? 'it' : 'them'} and save.
            </span>
          </p>
        )}
        {allPermissions.length === 0 ? (
          <WidgetPermissionList manifest={manifest} emptyText="This widget asks for nothing beyond drawing its own UI" />
        ) : (
          <>
            <WidgetPermissionList manifest={manifest} selectable selected={granted} onToggle={togglePermission} />
            <p className="wg-controls__hint">Untick anything you don't want to allow. The widget keeps working, but that capability is refused when it asks.</p>
          </>
        )}
      </div>

      {(manifest?.connections ?? []).length > 0 && (
        <div className="wg-controls__block">
          <span className="wg-perm-title">Connected services</span>
          <p className="wg-controls__hint" style={{ marginTop: 0 }}>
            The widget calls these through Momentum AMP — it never sees the key or password you enter here.
            {' '}<button type="button" onClick={() => setShowConnectDocs(true)} style={{ color: 'inherit', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer' }}>Learn more</button>.
          </p>
          {showConnectDocs && (<WidgetDocsModal title="Connecting a widget to an outside service" url={WIDGET_INSTALL_DOCS_URL} onClose={() => setShowConnectDocs(false)}/>)}
          {(manifest?.connections ?? []).map(c => {
            const connected = configuredKeys.has(c.key);
            const draft = connectionValues[c.key] ?? '';
            const disconnecting = draft === '' && Object.prototype.hasOwnProperty.call(connectionValues, c.key);
            return (
              <div key={c.key} className="wg-connection-row">
                <div className="wg-connection-row__head">
                  <span className="wg-connection-row__label">{c.label}</span>
                  <span className={`wg-chip ${connected && !disconnecting ? 'wg-chip--connected' : ''}`}>
                    {disconnecting ? 'Will disconnect' : connected ? '🔒 Connected' : 'Not connected'}
                  </span>
                </div>
                {c.hint && <p className="wg-controls__hint" style={{ margin: '2px 0 6px' }}>{c.hint}</p>}
                <div className="wg-connection-row__input">
                  <input
                    type="password"
                    autoComplete="off"
                    placeholder={connected ? 'Enter a new value to replace it' : `Paste your ${c.label} key`}
                    value={draft}
                    onChange={e => onConnectionValuesChange({ ...connectionValues, [c.key]: e.target.value })}
                  />
                  {connected && (
                    <button
                      type="button"
                      className="mt-btn mt-btn--secondary mt-btn--sm"
                      onClick={() => onConnectionValuesChange({ ...connectionValues, [c.key]: '' })}
                    >
                      Disconnect
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <p className="wg-controls__hint">Changes here are written when you click Save below.</p>
        </div>
      )}

      <div className="wg-controls__block">
        <span className="wg-perm-title">Show this widget on</span>
        <div className="wg-controls__surfaces">
          {availableSurfaces.map(s => {
            // An empty list is stored as "no override", which brought every surface back. To show
            // it nowhere, pause it instead.
            const isLastShown = shownOn.has(s) && shownOn.size === 1;
            return (
              <label key={s} className="wg-controls__check" title={isLastShown ? 'At least one place is required — to hide it everywhere, pause it below' : undefined}>
                <input type="checkbox" aria-label={`Show on ${SURFACE_LABEL[s]}`} checked={shownOn.has(s)} disabled={isLastShown} onChange={e => toggleSurface(s, e.target.checked)} />
                <span>{SURFACE_LABEL[s]}</span>
              </label>
            );
          })}
        </div>
        {availableSurfaces.length > 1 && shownOn.size === 1 && (
          <p className="wg-controls__hint">To hide it everywhere, pause it below.</p>
        )}
      </div>

      <div className="wg-controls__block wg-controls__row">
        <label className="wg-controls__check">
          <input type="checkbox" aria-label="Pause this widget" checked={controls.paused} onChange={e => onChange({ ...controls, paused: e.target.checked })} />
          <span><strong>Pause this widget</strong> — stays installed, but doesn't load anywhere until you resume it.</span>
        </label>
      </div>

      <div className="wg-controls__block wg-controls__row">
        <button type="button" className="mt-btn mt-btn--secondary mt-btn--sm" onClick={resetStorage}>Reset stored data</button>
        <span className="wg-controls__hint">{storageMsg ?? 'Clears what this widget saved for you on this device (Momentum.storage).'}</span>
      </div>
    </div>
  );
};

export default WidgetControlsPanel;
